import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useContact } from '../hooks/useContact';
import { useContacts } from '../hooks/useContacts';
import { useDeals } from '../hooks/useDeals';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ContactModal from '../components/contacts/ContactModal';
import { formatCurrency, getInitials } from '../utils/formatters';

const ContactDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contactId = searchParams.get('id');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load actual contact data from Firestore
  const { contact, loading: contactLoading } = useContact(contactId);
  const { updateContact, deleteContact } = useContacts();
  const { deals } = useDeals();

  // Filter deals that include this contact
  const contactDeals = useMemo(() => {
    if (!contactId || !deals) return [];
    return deals.filter(deal =>
      deal.contactIds && deal.contactIds.includes(contactId)
    );
  }, [deals, contactId]);

  if (contactLoading || !contact) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" message="Loading contact..." />
      </div>
    );
  }

  const getStageColor = (stage) => {
    const colors = {
      'Contacts': 'bg-gray-100 dark:bg-gray-700/30 text-gray-700 dark:text-gray-300 ring-gray-500/10',
      'Proposal': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-blue-700/10',
      'Negotiation': 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-amber-700/10',
      'Won': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-700/10',
      'Lost': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-700/10'
    };
    return colors[stage] || colors['Contacts'];
  };

  const handleEditContact = async (formData) => {
    try {
      await updateContact(contactId, formData);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating contact:', error);
      alert('Failed to update contact. Please try again.');
    }
  };

  const handleDeleteContact = async () => {
    setIsDeleting(true);
    try {
      await deleteContact(contactId);
      navigate('/contacts');
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-background-light dark:bg-background-dark relative">
      {/* Top Header Area */}
      <div className="shrink-0 px-6 py-5 border-b border-border-dark/50 bg-background-light dark:bg-background-dark z-10">
        <div className="max-w-[1200px] mx-auto w-full flex flex-col gap-4">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="flex">
            <ol className="flex items-center space-x-2">
              <li>
                <button
                  onClick={() => navigate('/contacts')}
                  className="text-slate-500 dark:text-text-secondary hover:text-primary text-sm font-medium transition-colors"
                >
                  Contacts
                </button>
              </li>
              <li><span className="text-slate-500 dark:text-text-secondary text-sm">/</span></li>
              <li><span className="text-slate-900 dark:text-white text-sm font-medium">{contact.name}</span></li>
            </ol>
          </nav>

          {/* Profile Header & Actions */}
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-border-dark p-6 shadow-sm">
            <div className="flex flex-col md:flex-row gap-6 justify-between items-start md:items-center">
              <div className="flex gap-5 items-center">
                <div className="relative">
                  <div className="size-20 md:size-24 rounded-full bg-slate-200 dark:bg-[#324d67] flex items-center justify-center text-slate-600 dark:text-white font-bold text-2xl shadow-md">
                    {getInitials(contact.name)}
                  </div>
                  <div className="absolute bottom-1 right-1 size-5 bg-green-500 border-2 border-white dark:border-surface-dark rounded-full" title="Active"></div>
                </div>
                <div className="flex flex-col">
                  <h1 className="text-slate-900 dark:text-white text-2xl font-bold leading-tight">{contact.name}</h1>
                  <p className="text-slate-500 dark:text-text-secondary text-base font-normal">{contact.company || 'No company'}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStageColor(contact.status)}`}>
                      {contact.status}
                    </span>
                    <span className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700/30 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-inset ring-gray-500/10 dark:ring-gray-400/20">
                      {contactDeals.length} {contactDeals.length === 1 ? 'Deal' : 'Deals'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <button className="flex-1 md:flex-none items-center justify-center rounded-lg h-10 px-4 border border-slate-200 dark:border-border-dark bg-white dark:bg-[#233648] text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-[#2c445a] text-sm font-semibold transition-colors flex gap-2">
                  <span className="material-symbols-outlined text-[20px]">call</span>
                  <span className="truncate">Call</span>
                </button>
                <button className="flex-1 md:flex-none items-center justify-center rounded-lg h-10 px-4 bg-primary text-white hover:bg-blue-600 text-sm font-semibold transition-colors flex gap-2 shadow-sm">
                  <span className="material-symbols-outlined text-[20px]">mail</span>
                  <span className="truncate">Email</span>
                </button>
                <button
                  onClick={() => navigate('/contacts')}
                  className="flex items-center justify-center rounded-lg size-10 border border-slate-200 dark:border-border-dark bg-white dark:bg-[#233648] text-slate-500 dark:text-text-secondary hover:text-slate-700 dark:hover:text-white transition-colors"
                  title="Back to contacts"
                >
                  <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
        <div className="max-w-[1200px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Sidebar: Contact Details */}
            <aside className="flex flex-col gap-6">
              {/* Info Card */}
              <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-border-dark shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 dark:border-border-dark flex justify-between items-center">
                  <h3 className="text-slate-900 dark:text-white font-bold text-base">Information</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsEditModalOpen(true)}
                      className="text-primary text-sm font-medium hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="text-red-500 hover:text-red-600 text-sm font-medium hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="p-5 flex flex-col gap-5">
                  <div className="flex flex-col gap-1">
                    <span className="text-slate-500 dark:text-text-secondary text-xs font-medium uppercase tracking-wider">Email</span>
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-slate-400 dark:text-text-secondary text-[18px]">mail</span>
                      <a className="text-slate-900 dark:text-white text-sm hover:text-primary transition-colors" href={`mailto:${contact.email}`}>
                        {contact.email}
                      </a>
                    </div>
                  </div>
                  {contact.phone && (
                    <div className="flex flex-col gap-1">
                      <span className="text-slate-500 dark:text-text-secondary text-xs font-medium uppercase tracking-wider">Phone</span>
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-slate-400 dark:text-text-secondary text-[18px]">phone</span>
                        <a className="text-slate-900 dark:text-white text-sm hover:text-primary transition-colors" href={`tel:${contact.phone}`}>
                          {contact.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  {contact.company && (
                    <div className="flex flex-col gap-1">
                      <span className="text-slate-500 dark:text-text-secondary text-xs font-medium uppercase tracking-wider">Company</span>
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-slate-400 dark:text-text-secondary text-[18px]">business</span>
                        <span className="text-slate-900 dark:text-white text-sm">{contact.company}</span>
                      </div>
                    </div>
                  )}
                  {contact.notes && (
                    <div className="flex flex-col gap-1">
                      <span className="text-slate-500 dark:text-text-secondary text-xs font-medium uppercase tracking-wider">Notes</span>
                      <p className="text-slate-900 dark:text-white text-sm leading-relaxed">{contact.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </aside>

            {/* Right Panel: Deals List */}
            <div className="col-span-1 lg:col-span-2 flex flex-col gap-6">
              {/* Deals List */}
              <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-border-dark shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 dark:border-border-dark">
                  <h3 className="text-slate-900 dark:text-white font-bold text-base">Associated Deals</h3>
                </div>
                <div className="p-5">
                  {contactDeals.length === 0 ? (
                    <div className="text-center py-12">
                      <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-[48px] mb-3 block">handshake</span>
                      <p className="text-slate-500 dark:text-text-secondary text-sm">No deals associated with this contact yet</p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {contactDeals.map((deal) => (
                        <div
                          key={deal.id}
                          onClick={() => navigate(`/deal-details?id=${deal.id}`)}
                          className="border border-slate-200 dark:border-border-dark rounded-lg p-4 hover:bg-slate-50 dark:hover:bg-[#1a2632] transition-colors cursor-pointer group"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-slate-900 dark:text-white font-semibold text-base group-hover:text-primary transition-colors">
                              {deal.title}
                            </h4>
                            <span className="text-slate-900 dark:text-white font-bold text-base">
                              {formatCurrency(deal.value)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStageColor(deal.stage)}`}>
                              {deal.stage}
                            </span>
                            {deal.category && (
                              <span className="text-slate-500 dark:text-text-secondary text-xs">
                                • {deal.category}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-text-secondary">
                            <span>
                              Created {new Date(deal.createdAt.seconds * 1000).toLocaleDateString('en-US')}
                            </span>
                            <span className="flex items-center gap-1 text-primary font-medium group-hover:underline">
                              View Details
                              <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Contact Modal */}
      <ContactModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        contact={contact}
        onSubmit={handleEditContact}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Delete Contact</h3>
            <p className="text-slate-600 dark:text-text-secondary mb-6">
              Are you sure you want to delete <strong>{contact?.name}</strong>? This action cannot be undone.
              {contactDeals.length > 0 && (
                <span className="block mt-2 text-amber-600 dark:text-amber-400">
                  Warning: This contact is associated with {contactDeals.length} deal{contactDeals.length !== 1 ? 's' : ''}.
                </span>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-white bg-white dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] rounded-lg hover:bg-gray-50 dark:hover:bg-[#233648] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteContact}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <span className="material-symbols-outlined text-[18px] animate-spin">refresh</span>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete Contact</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDetails;
